import React, { useRef, useState } from 'react';
import { IcoDownload, IcoTrash, IcoLink, IcoPlus2 } from './EditIcons';
import { Label, InputBox, SelectBox, FieldRow, Field } from './EditShared';
import { deleteTalentWork, deleteTalentMedia, uploadFile, addTalentMedia } from '../../../../api';
import { nextLocalWorkId } from '../../../../shared/utils/workDedupe';
import ImageLightbox from './ImageLightbox';
import styles from './EditFilmographySection.module.css';

const MAX_STILLCUTS = 4;

function StillcutThumb({ shot, onRemove, onClick }) {
  return (
    <div style={{ position: 'relative', width: 120, height: 120, borderRadius: '10px', overflow: 'hidden', border: '1px solid #eeeeee', flexShrink: 0 }}>
      <img
        src={shot.preview}
        alt="stillcut"
        onClick={onClick}
        style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer', display: 'block' }}
      />
      <button
        onClick={onRemove}
        style={{
          position: 'absolute', top: 6, right: 6,
          width: 24, height: 24, borderRadius: '50%',
          background: 'rgba(0,0,0,0.55)', border: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', padding: 0,
        }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6l12 12" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"/>
        </svg>
      </button>
    </div>
  );
}

function WorkCard({ work, workNumber, talentProfileId, onDelete, onUpdate, deferStillcutUpload = false }) {
  const stillcutRef = useRef(null);
  const [isUploadingStillcut, setIsUploadingStillcut] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const stillcuts = work.stillcuts || [];
  const canAddMore = stillcuts.length < MAX_STILLCUTS;

  function removeStillcut(idx) {
    const shot = stillcuts[idx];
    const nextStillcuts = stillcuts.filter((_, i) => i !== idx);
    onUpdate('stillcuts', nextStillcuts);
    if (shot.mediaId && !deferStillcutUpload) {
      deleteTalentMedia(shot.mediaId).catch(() => alert('사진 삭제에 실패했습니다.'));
    } else if (shot.mediaId && deferStillcutUpload) {
      onUpdate('_deletedStillcutMediaIds', [...(work._deletedStillcutMediaIds || []), shot.mediaId]);
    }
  }

  async function handleAddStillcut(e) {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (deferStillcutUpload) {
      onUpdate('stillcuts', [...stillcuts, { mediaId: null, preview: URL.createObjectURL(file), file }]);
      return;
    }
    if (!talentProfileId) return;
    setIsUploadingStillcut(true);
    try {
      const { fileEntityId, url } = await uploadFile(file, 'IMAGE');
      const media = await addTalentMedia({
        talentProfileId: Number(talentProfileId),
        fileEntityId,
        mediaType: 'IMAGE',
        photoType: 'FILMOGRAPHY',
        workId: work.id,
      });
      onUpdate('stillcuts', [...stillcuts, { mediaId: media?.id ?? null, preview: url }]);
    } catch {
      alert('스틸컷 업로드에 실패했습니다.');
    } finally {
      setIsUploadingStillcut(false);
    }
  }

  return (
    <div className={styles.workCard}>
      <div className={styles.cardHeader}>
        <span style={{ fontSize: '24px', fontWeight: 600, color: '#fff' }}>작품 {workNumber}</span>
        <button
          onClick={onDelete}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}
        >
          <IcoTrash color="#fff" />
        </button>
      </div>

      <div className={styles.cardBody}>
        <FieldRow>
          <Field label="촬영 연도" required>
            <InputBox value={work.year} onChange={(e) => onUpdate('year', e.target.value)} />
          </Field>
          <Field label="작품 제목" required>
            <InputBox value={work.title} onChange={(e) => onUpdate('title', e.target.value)} />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field label="제작사/플랫폼" required>
            <InputBox value={work.company} onChange={(e) => onUpdate('company', e.target.value)} />
          </Field>
          <Field label="매체 유형" required>
            <SelectBox
              value={work.mediaType}
              onChange={(e) => onUpdate('mediaType', e.target.value)}
              options={[
                { value: '', label: '매체 유형을 선택해주세요' },
                { value: '장편상업영화', label: '장편상업영화' },
                { value: '장편독립영화', label: '장편독립영화' },
                { value: '단편영화', label: '단편영화' },
                { value: 'TV/OTT', label: 'TV/OTT' },
                { value: '숏폼드라마', label: '숏폼드라마' },
                { value: '웹드라마', label: '웹드라마' },
                { value: '광고', label: '광고' },
                { value: '기타', label: '기타' },
              ]}
            />
          </Field>
          <Field label="배역" required>
            <InputBox value={work.role} onChange={(e) => onUpdate('role', e.target.value)} />
          </Field>
        </FieldRow>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
          <Label>스틸컷 (최대 {MAX_STILLCUTS}장)</Label>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <input ref={stillcutRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAddStillcut} />
            {stillcuts.map((shot, idx) => (
              <StillcutThumb
                key={shot.mediaId ?? shot.preview}
                shot={shot}
                onClick={() => setLightboxIndex(idx)}
                onRemove={() => removeStillcut(idx)}
              />
            ))}
            {canAddMore && (
              <div
                onClick={() => !isUploadingStillcut && stillcutRef.current?.click()}
                style={{
                  width: 120, height: 120, background: '#f8f8f8', border: '1px solid #eeeeee',
                  borderRadius: '10px', display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer',
                  overflow: 'hidden', flexShrink: 0,
                }}
              >
                <IcoDownload />
                <span style={{ fontSize: '13px', color: '#A9A9B2', textAlign: 'center' }}>
                  {isUploadingStillcut ? '업로드 중...' : '사진 첨부'}
                </span>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
          <Label>영상(선택)</Label>
          <div style={{
            height: 60, background: '#f8f8f8', border: '1px solid #eeeeee', borderRadius: '10px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
          }}>
            <IcoLink />
            <input
              type = "text"
              onClick={(e) => e.stopPropagation()} // 클릭이 부모로 전달되는 것을 방지
              value={work.videoUrl}
              onChange={e => onUpdate('videoUrl', e.target.value)}
              placeholder="Youtube, Vimeo 등 영상 URL을 입력하세요."
              style={{
                border: 'none', background: 'transparent', outline: 'none',
                fontFamily: 'Pretendard', fontSize: '18px', letterSpacing: '-0.04em', color: '#222',
                textAlign: 'center', width: '50%',
              }}
            />
          </div>
        </div>

      </div>

      <ImageLightbox
        url={lightboxIndex == null ? null : stillcuts[lightboxIndex]?.preview}
        alt={`${work.title || '작품'} 스틸컷`}
        onClose={() => setLightboxIndex(null)}
        onPrev={stillcuts.length > 1 ? () => setLightboxIndex((i) => (i - 1 + stillcuts.length) % stillcuts.length) : undefined}
        onNext={stillcuts.length > 1 ? () => setLightboxIndex((i) => (i + 1) % stillcuts.length) : undefined}
        currentIndex={lightboxIndex == null ? undefined : lightboxIndex + 1}
        totalCount={stillcuts.length}
        large
        fullScreen
      />
    </div>
  );
}

export default function EditFilmographySection({
  talentProfileId,
  works,
  setWorks,
  hideHeader = false,
  hideAddButton = false,
  deferStillcutUpload = false,
}) {
  function addWork() {
    // 서버 ID(양수)와 겹치지 않는 임시 ID. max+1을 쓰면 실제 작품 ID와 충돌할 수 있다.
    const nextId = nextLocalWorkId(works);
    setWorks(prev => [...prev, {
      id: nextId, year: '', title: '', company: '', mediaType: '', role: '',
      videoUrl: '', stillcuts: [], videoFileName: null, videoFileId: null, _isNew: true,
    }]);
  }

  async function deleteWork(id) {
    const work = works.find(w => w.id === id);
    if (work && !work._isNew) {
      try {
        await deleteTalentWork(id);
      } catch {
        alert('작품 삭제에 실패했습니다.');
        return;
      }
    }
    setWorks(prev => prev.filter(w => w.id !== id));
  }

  function updateWork(id, field, value) {
    setWorks(prev => prev.map(w =>
      w.id === id ? { ...w, [field]: value, ...(!w._isNew && { _isUpdated: true }) } : w
    ));
  }

  return (
    <div className={styles.sectionContainer}>
      <div className={styles.header} style={hideHeader ? { justifyContent: 'flex-end' } : undefined}>
        {!hideHeader && (
          <div style={{ flex: 1 }}>
            <h2 className={styles.title}>필모그래피</h2>
            <p className={styles.description}>출연 작품을 추가하세요.</p>
          </div>
        )}
        {!hideAddButton && (
          <button onClick={addWork} className={styles.addButton}>
            <IcoPlus2 /> 추가
          </button>
        )}
      </div>

      <div className={styles.workList}>
        {works.map((work, idx) => (
          <WorkCard
            key={work.id}
            work={work}
            workNumber={idx + 1}
            talentProfileId={talentProfileId}
            onDelete={() => deleteWork(work.id)}
            onUpdate={(field, value) => updateWork(work.id, field, value)}
            deferStillcutUpload={deferStillcutUpload}
          />
        ))}
      </div>
    </div>
  );
}
